import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { FundRawRecord } from './types';
import { ensureUrlProtocol, cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

// For type-safe column definitions
const columnHelper = createColumnHelper<FundRawRecord>();

// Common cell content styles
const cellTextStyles = "text-sm w-full whitespace-normal break-words hyphens-auto";
const codeTextStyles = "text-xs font-mono whitespace-normal break-all hyphens-auto overflow-visible";
const linkStyles = "text-blue-600 dark:text-blue-400 hover:text-primary hover:underline";

// Helper to sanitize URLs
const sanitizeUrl = (url: string) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

// Reusable header component with sort indicators
export const SortableHeader = ({ column, title }: { column: any; title: string }) => {
  const isSorted = column.getIsSorted();
  return (
    <div className="flex items-center gap-1 group select-none min-h-[2rem]">
      <span className="text-xs font-medium">{title}</span>
      {column.getCanSort() && (
        <span className="opacity-70 group-hover:opacity-100">
          {isSorted === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : isSorted === 'desc' ? (
            <ArrowDown className="h-3.5 w-3.5" />
          ) : (
            <ArrowUpDown className="h-3.5 w-3.5 opacity-40 group-hover:opacity-70" />
          )}
        </span>
      )}
    </div>
  );
};

export const getDefaultColumns = (bulkSelection?: {
  isSelected: (id: string) => boolean;
  toggleItem: (id: string) => void;
  toggleAll: (allIds: string[]) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  currentPageIds: string[];
}): ColumnDef<FundRawRecord, any>[] => {
  const columns: ColumnDef<FundRawRecord, any>[] = [];
  
  // Add checkbox column if bulk selection is enabled
  if (bulkSelection) {
    columns.push(
      columnHelper.display({
        id: 'select',
        header: () => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={bulkSelection.isAllSelected}
              ref={(el) => {
                if (el) {
                  const input = el.querySelector('input');
                  if (input) input.indeterminate = bulkSelection.isIndeterminate;
                }
              }}
              onCheckedChange={() => bulkSelection.toggleAll(bulkSelection.currentPageIds)}
              aria-label="Select all"
              className="h-3.5 w-3.5"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={bulkSelection.isSelected(row.original.id)}
              onCheckedChange={() => bulkSelection.toggleItem(row.original.id)}
              aria-label="Select row"
              className="h-3.5 w-3.5"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ),
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableResizing: false,
        enableSorting: false,
        meta: {
          className: 'px-3 py-1 text-center'
        }
      })
    );
  }
  
  columns.push(
    columnHelper.accessor(row => `${row.management_company_code || ''} ${row.fund_code || ''}`.trim() || '-', {
      id: 'code',
      header: ({ column }) => <SortableHeader column={column} title="Code" />,
      size: 90,
      minSize: 90,
      maxSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'pl-4 pr-2 py-2 align-middle'
      },
      cell: info => (
        <span className={codeTextStyles}>{info.getValue()}</span>
      )
    }),
    columnHelper.accessor('fund_name', {
      id: 'fund_name',
      header: ({ column }) => <SortableHeader column={column} title="Fund Name" />,
      size: 250,
      minSize: 150,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-middle'
      },
      cell: info => {
        const fund = info.row.original;
        const hasValidUrl = fund.fund_marketing_url && 
          typeof fund.fund_marketing_url === 'string' && 
          fund.fund_marketing_url.trim() !== '';
          
        if (hasValidUrl) {
          return (
            <a
              href={ensureUrlProtocol(fund.fund_marketing_url!)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${cellTextStyles} ${linkStyles}`}
              title={`${fund.fund_name || '-'} (${sanitizeUrl(fund.fund_marketing_url!)})`}
            >
              {fund.fund_name || '-'}
            </a>
          );
        }
        
        return <span className={cellTextStyles}>{fund.fund_name || '-'}</span>;
      }
    }),
    columnHelper.accessor('company_name', {
      id: 'company',
      header: ({ column }) => <SortableHeader column={column} title="Company" />,
      size: 200,
      minSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-middle hidden md:table-cell'
      },
      cell: info => {
        const fund = info.row.original;
        const hasValidUrl = fund.fund_companies?.website_url && 
          typeof fund.fund_companies.website_url === 'string' && 
          fund.fund_companies.website_url.trim() !== '';
          
        if (hasValidUrl) {
          return (
            <a
              href={ensureUrlProtocol(fund.fund_companies.website_url!)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${cellTextStyles} ${linkStyles}`}
            >
              {fund.company_name || '-'}
            </a>
          );
        }
        
        return <span className={cellTextStyles}>{fund.company_name || '-'}</span>;
      }
    }),
    columnHelper.accessor('fund_type', {
      id: 'fundType',
      header: ({ column }) => <SortableHeader column={column} title="Fund Type" />,
      size: 130,
      minSize: 100,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-middle hidden md:table-cell'
      },
      cell: info => (
        <span className={cellTextStyles}>{info.getValue() || '-'}</span>
      )
    }),
    columnHelper.accessor('sa_sub_asset_class', {
      id: 'strategy',
      header: ({ column }) => <SortableHeader column={column} title="Strategy" />,
      size: 180,
      minSize: 120,
      enableResizing: true,
      enableSorting: true,
      meta: {
        className: 'px-4 py-2 align-middle hidden lg:table-cell'
      },
      cell: info => (
        <span className={cellTextStyles}>{info.getValue() || '-'}</span>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: ({ column }) => <div className="text-right flex items-center justify-end h-full"></div>,
      size: 60,
      minSize: 60,
      maxSize: 80,
      meta: {
        className: 'pl-2 pr-4 py-2 text-right align-middle'
      },
      enableResizing: true,
      enableSorting: false,
      cell: () => (
        <span>•••</span> // Will be replaced with actual actions
      )
    }),
  );
  
  return columns;
};

// Custom hook to save and load column preferences
export function useTablePreferences(tableId: string) {
  // Load saved preferences from localStorage
  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem(`table_prefs_${tableId}`);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading table preferences:', error);
      return null;
    }
  };
  
  // Save preferences to localStorage
  const savePreferences = (columnSizing: Record<string, number>, columnVisibility: Record<string, boolean>) => {
    try {
      const key = `table_prefs_${tableId}`;
      let existing: any = null;
      try {
        const raw = localStorage.getItem(key);
        existing = raw ? JSON.parse(raw) : null;
      } catch {}
      const merged = {
        ...(existing || {}),
        columnSizing,
        columnVisibility,
      };
      localStorage.setItem(key, JSON.stringify(merged));
    } catch (error) {
      console.error('Error saving table preferences:', error);
    }
  };
  
  return { loadPreferences, savePreferences };
}
